import { useRef, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { MoveHorizontal } from "lucide-react";
import { useLang } from "../i18n";

const galleryImages = [
  "https://images.pexels.com/photos/14231678/pexels-photo-14231678.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "https://images.pexels.com/photos/3892898/pexels-photo-3892898.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "https://images.pexels.com/photos/27692895/pexels-photo-27692895.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "https://images.pexels.com/photos/17632108/pexels-photo-17632108.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
];

const compareImg =
  "https://images.pexels.com/photos/3354648/pexels-photo-3354648.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400";

const BeforeAfter = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const [pos, setPos] = useState(50);
  const dragging = useRef(false);

  const move = useCallback((clientX) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    const p = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(2, Math.min(98, p)));
  }, []);

  return (
    <div
      ref={ref}
      data-testid="before-after-slider"
      className="relative w-full aspect-[16/10] rounded-2xl overflow-hidden gold-border select-none cursor-ew-resize"
      onMouseMove={(e) => dragging.current && move(e.clientX)}
      onMouseUp={() => (dragging.current = false)}
      onMouseLeave={() => (dragging.current = false)}
      onMouseDown={(e) => {
        dragging.current = true;
        move(e.clientX);
      }}
      onTouchMove={(e) => move(e.touches[0].clientX)}
    >
      {/* after (full) */}
      <img src={compareImg} alt="after" className="absolute inset-0 w-full h-full object-cover" draggable={false} />
      <span className="absolute top-4 right-4 z-10 text-[0.65rem] font-mono uppercase tracking-widest px-3 py-1 rounded-full bg-black/60 text-[var(--c-gold)] border border-[var(--c-gold)]/30">
        {t.gallery.after}
      </span>

      {/* before (clipped) */}
      <div className="absolute inset-0 overflow-hidden" style={{ width: `${pos}%` }}>
        <img
          src={compareImg}
          alt="before"
          className="absolute inset-0 h-full object-cover"
          style={{ width: ref.current?.clientWidth || "100%", filter: "grayscale(1) brightness(0.55) contrast(0.9)" }}
          draggable={false}
        />
        <span className="absolute top-4 left-4 text-[0.65rem] font-mono uppercase tracking-widest px-3 py-1 rounded-full bg-black/60 text-neutral-300 border border-white/15">
          {t.gallery.before}
        </span>
      </div>

      {/* handle */}
      <div className="absolute top-0 bottom-0 z-20" style={{ left: `${pos}%`, transform: "translateX(-50%)" }}>
        <div className="w-0.5 h-full bg-[var(--c-gold)]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-[var(--c-gold)] flex items-center justify-center shadow-lg">
          <MoveHorizontal size={18} className="text-black" />
        </div>
      </div>
    </div>
  );
};

export const Gallery = () => {
  const { t } = useLang();
  return (
    <section id="gallery" className="relative py-28 md:py-36 bg-[var(--c-bg-2)]">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-10 bg-[var(--c-gold)]" />
              <span className="overline">{t.gallery.tag}</span>
            </div>
            <h2 className="font-display text-5xl sm:text-6xl font-semibold text-white">{t.gallery.title}</h2>
          </div>
          <p className="text-neutral-400 max-w-sm md:text-right">{t.gallery.sub}</p>
        </div>

        <div className="grid lg:grid-cols-5 gap-5">
          <div className="lg:col-span-3">
            <BeforeAfter />
          </div>
          <div className="lg:col-span-2 grid grid-cols-2 gap-5">
            {galleryImages.map((src, i) => (
              <motion.div
                key={i}
                data-testid={`gallery-img-${i}`}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className="relative rounded-2xl overflow-hidden gold-border aspect-square group"
              >
                <img
                  src={src}
                  alt={`detailing ${i + 1}`}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
